import { motion, useInView } from "framer-motion";
import { useRef, useState, useEffect } from "react";
import { BarChart3 } from "lucide-react";
import KOPipelineStep from "./KOPipelineStep";
import { affordableKeywords } from "./mockData";

const KOVolumeSorting = () => {
  const sectionRef = useRef<HTMLDivElement>(null);
  const isInView = useInView(sectionRef, { once: true, margin: "-100px" });
  const [sorted, setSorted] = useState(false);

  useEffect(() => {
    if (isInView) {
      const timer = setTimeout(() => setSorted(true), 600);
      return () => clearTimeout(timer);
    }
  }, [isInView]);

  const maxVolume = Math.max(...affordableKeywords.map((k) => k.searchVolume));

  const keywords = sorted
    ? [...affordableKeywords].sort((a, b) => b.searchVolume - a.searchVolume)
    : affordableKeywords;

  return (
    <section className="py-24 px-4 bg-muted/30" ref={sectionRef}>
      <div className="container mx-auto max-w-6xl">
        <KOPipelineStep step={3} title="Prioritise by Search Volume">
          <div className="flex items-center gap-3 mb-6">
            <BarChart3 className="h-5 w-5 text-primary" />
            <span className="text-sm font-medium text-muted-foreground">
              {sorted
                ? "Sorted by highest search volume — maximum exposure within your budget"
                : "Sorting keywords by search volume..."}
            </span>
          </div>

          <div className="bg-white rounded-xl p-6 border border-border shadow-sm overflow-hidden">
            <div className="space-y-2">
              {keywords.map((kw, index) => (
                <motion.div
                  key={kw.keyword}
                  layout
                  className="flex items-center gap-4"
                  transition={{
                    layout: { type: "spring", stiffness: 200, damping: 25 },
                  }}
                >
                  <span className="text-xs text-muted-foreground w-6 text-right font-mono">
                    {sorted ? index + 1 : ""}
                  </span>
                  <span className="text-sm font-medium w-48 truncate">{kw.keyword}</span>
                  <div className="flex-1 h-7 bg-muted/50 rounded-full overflow-hidden">
                    <motion.div
                      className={`h-full rounded-full flex items-center pl-3 ${
                        sorted && index < 3
                          ? "bg-primary"
                          : sorted && index < 8
                          ? "bg-primary/70"
                          : "bg-primary/40"
                      }`}
                      initial={{ width: 0 }}
                      animate={{
                        width: `${(kw.searchVolume / maxVolume) * 100}%`,
                      }}
                      transition={{
                        duration: 0.8,
                        delay: sorted ? index * 0.05 : 0,
                        ease: "easeOut",
                      }}
                    >
                      <span className="text-[10px] font-bold text-white whitespace-nowrap">
                        {(kw.searchVolume / 1000).toFixed(1)}K
                      </span>
                    </motion.div>
                  </div>
                  <span className="text-xs text-green-600 font-medium w-16 text-right">
                    ${kw.bidEstimate.toFixed(2)}
                  </span>
                </motion.div>
              ))}
            </div>
          </div>

          {sorted && (
            <motion.div
              className="mt-4 flex items-center gap-2"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.0 }}
            >
              <div className="h-2 w-2 rounded-full bg-primary animate-pulse" />
              <span className="text-sm text-primary font-medium">
                Top keywords deliver the most impressions at the lowest cost
              </span>
            </motion.div>
          )}

          {/* SEO text */}
          <motion.p
            className="mt-8 text-muted-foreground leading-relaxed max-w-4xl"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3 }}
          >
            Once expensive keywords are removed, the remaining candidates are ranked by monthly
            search volume. This ensures your budget is allocated to keywords that real people are
            actually searching for. High-volume, affordable keywords are the sweet spot — they
            deliver maximum visibility without overspending. This prioritisation means your ads
            appear more often, to more potential customers, throughout the entire day.
          </motion.p>
        </KOPipelineStep>
      </div>
    </section>
  );
};

export default KOVolumeSorting;
